import PropTypes from "prop-types";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";

// components
import RadioInput from "@/components/RadioInput/RadioInput";
import Checkbox from "@/components/Checkbox/Checkbox";
import Stepper from "@/components/Stepper/Stepper";
import BrainCoverageViz from "@/components/BrainCoverageViz/BrainCoverageViz";
import StepperPresets from "@/components/Stepper/StepperPresets";

// -----------------------------------------------------------------------------

export default function Connectomics({ isLinkMode, expId }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;

	// Select the experiment data from the store
	const experiment = useStore((state) =>
		state.connectomicsExperimentsArr.find((exp) => exp.id === expId),
	);

	// Select each action individually
	const setConnectomicsValues = useStore(
		(state) => state.setConnectomicsValues,
	);
	// Select the specific action for microscopy approach
	const setMicroscopyApproach = useStore(
		(state) => state.setMicroscopyApproach,
	);

	// Selector logic for molecular annotation availability, derived from the experiment data
	function isMolecularAnnotationAvailable() {
		if (!experiment) return false; // Handle case where experiment might not be found yet
		return (
			experiment.microscopyApproach === "expansion-microscopy" ||
			experiment.microscopyApproach === "barcoding"
		);
	}

	// -----------------------------------------------------------------------------

	return (
		<div className="experiment-container">
			{/* Brain coverage stepper */}
			<div className="stepper-w-viz">
				<Stepper
					label="Brain Coverage [%]"
					id={`connectomics-coverage-${expId}`} // Ensure unique ID per experiment
					value={experiment.connectomicsCoverage}
					min={0}
					max={100}
					step={5}
					onChange={(value) =>
						setConnectomicsValues(expId, "connectomicsCoverage", value)
					}
				/>
				{/* Icon */}
				<BrainCoverageViz coverage={experiment.connectomicsCoverage} />
				{/* Presets */}
				<StepperPresets
					label="Brain Coverage [%]"
					presets={[25, 75, 100]}
					onChange={(value) =>
						setConnectomicsValues(expId, "connectomicsCoverage", value)
					}
				/>
			</div>

			{/* Reconstruction Resolution */}
			<fieldset className="radio-group">
				<legend>Reconstruction Resolution</legend>
				<RadioInput
					label="Synapse"
					value="synapse"
					name={`reconstruction-resolution-${expId}`} // Ensure unique name per experiment
					checked={experiment.reconstructionResolution === "synapse"}
					onChange={(e) =>
						setConnectomicsValues(
							expId,
							"reconstructionResolution",
							e.target.value,
						)
					}
				/>
				<RadioInput
					label="Synapse conservative"
					value="synapse-conservative"
					name={`reconstruction-resolution-${expId}`}
					checked={
						experiment.reconstructionResolution === "synapse-conservative"
					}
					onChange={(e) =>
						setConnectomicsValues(
							expId,
							"reconstructionResolution",
							e.target.value,
						)
					}
				/>
				<RadioInput
					label="Gap junction"
					value="gap-junction"
					name={`reconstruction-resolution-${expId}`}
					checked={experiment.reconstructionResolution === "gap-junction"}
					onChange={(e) =>
						setConnectomicsValues(
							expId,
							"reconstructionResolution",
							e.target.value,
						)
					}
				/>
			</fieldset>

			{/* Microscopy Approach */}
			<fieldset className="radio-group">
				<legend>Microscopy Approach</legend>
				<RadioInput
					label="Electron microscopy"
					value="electron-microscopy"
					name={`microscopy-approach-${expId}`} // Ensure unique name per experiment
					checked={experiment.microscopyApproach === "electron-microscopy"}
					onChange={(e) => setMicroscopyApproach(expId, e.target.value)}
				/>
				<RadioInput
					label="Expansion microscopy"
					value="expansion-microscopy"
					name={`microscopy-approach-${expId}`}
					checked={experiment.microscopyApproach === "expansion-microscopy"}
					onChange={(e) => setMicroscopyApproach(expId, e.target.value)}
				/>
				<RadioInput
					label="Barcoding"
					value="barcoding"
					name={`microscopy-approach-${expId}`}
					checked={experiment.microscopyApproach === "barcoding"}
					onChange={(e) => setMicroscopyApproach(expId, e.target.value)}
				/>
				<RadioInput
					label="X-ray"
					value="xray"
					name={`microscopy-approach-${expId}`}
					checked={experiment.microscopyApproach === "xray"}
					onChange={(e) => setMicroscopyApproach(expId, e.target.value)}
				/>
			</fieldset>

			{/* Optional options */}
			{isMolecularAnnotationAvailable() && (
				<fieldset className="mixed-fieldset">
					<legend>Optional</legend>

					<Checkbox
						label="Molecular annotation"
						value="molecular-annotation"
						// added id so that console doesn't complain
						id={`molecular-annotation-${expId}`} // Ensure unique ID per experiment
						checked={experiment.molecularAnnotation}
						onChange={(e) =>
							setConnectomicsValues(
								expId,
								"molecularAnnotation",
								e.target.checked,
							)
						}
						disabled={!isMolecularAnnotationAvailable()}
					/>

					<Stepper
						label="Number of molecules"
						id={`number-of-molecules-${expId}`} // Ensure unique ID per experiment
						value={experiment.numberOfMolecules}
						min={0}
						max={100}
						step={10}
						onChange={(value) =>
							setConnectomicsValues(expId, "numberOfMolecules", value)
						}
						disabled={
							!experiment.molecularAnnotation ||
							!isMolecularAnnotationAvailable()
						}
					/>
				</fieldset>
			)}
		</div>
	);
}

// -----------------------------------------------------------------------------

Connectomics.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
	expId: PropTypes.string.isRequired,
};
